import { useQuery } from '@apollo/client'
import AppBar from '@material-ui/core/AppBar'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import { useTheme } from '@material-ui/core/styles'
import React, { useMemo } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import { ACCOMMODATION_NAME_QUERY } from '../../graphql/accommodation'
import { RATEPLAN_NAME_QUERY } from '../../graphql/rateplan'
import { TabPanel } from '../../helpers/TabPanel'
import {
  sanitizeSupportDistribution,
  sanitizeSupportEntities
} from '../../helpers/supportLaunchContext'
import { useRegisterSupportScreen } from '../../helpers/supportScreenContext'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import ratePlanPagesRoleManagement from '../../staticRoleManagement/ratePlanRoleManagement'
import RatePlansPolicies from '../RatePlansPolicies'
import RateplanForm from '../RateplanForm'
import useStyles from './styles/index.styles'

export default function RateplanTabs() {
  const classes = useStyles()
  const theme = useTheme()
  const { id, page, accommodationID, roomTypeID, providerCode, propertyCode } =
    useParams<{
      id: string
      page: string
      accommodationID: string
      propertyCode: string
      roomTypeID: string
      providerCode: string
    }>()
  const {
    data: ratePlanNameData,
    loading: ratePlanNameDataLoading,
    error: ratePlanNameError
  } = useQuery<{
    ratePlanName: {
      name: string
      ratePlanCode?: string
      roomTypeCode?: string
    }
  }>(RATEPLAN_NAME_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationID,
      roomTypeId: roomTypeID,
      ratePlanId: id
    }
  })
  const {
    data: accommodationName,
    loading: accommodationNameLoading,
    error: accommodationNameError
  } = useQuery<{
    accommodationName: {
      name: string
      propertyCode?: string
    }
  }>(ACCOMMODATION_NAME_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationID
    }
  })
  const loadedRate = ratePlanNameData?.ratePlanName
  const loadedProperty = accommodationName?.accommodationName
  const supportReady =
    !ratePlanNameDataLoading &&
    !accommodationNameLoading &&
    !ratePlanNameError &&
    !accommodationNameError
  useRegisterSupportScreen({
    affectedEntities:
      supportReady && loadedProperty
        ? sanitizeSupportEntities([
            {
              type: 'accommodation',
              code: loadedProperty.propertyCode,
              label: loadedProperty.name
            }
          ])
        : [],
    relatedDistribution:
      supportReady && loadedRate && loadedProperty
        ? sanitizeSupportDistribution([
            {
              refType: 'RATE_PLAN',
              propertyCode: loadedProperty.propertyCode,
              roomTypeCode: loadedRate.roomTypeCode,
              ratePlanCode: loadedRate.ratePlanCode,
              label: loadedRate.name
            }
          ])
        : []
  })
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Accommodations'), link: '/accommodations' },
      {
        label: accommodationName?.accommodationName.name || '',
        link: `/accommodations/${accommodationID}/${propertyCode}/details`
      },
      {
        label: getTranslate('rateplans'),
        link: `/accommodations/${accommodationID}/${propertyCode}/rate-plans/list`
      },
      {
        label: ratePlanNameData?.ratePlanName.name || ''
      }
    ],
    [accommodationName, ratePlanNameData]
  )

  const tabsList = [
    {
      value: 'details',
      className: classes.tab,
      label: getTranslate('Details'),
      component: LocalizedLink,
      to: `/accommodations/${accommodationID}/${propertyCode}/room-type/${roomTypeID}/rate-plans/${id}/providerCode/${providerCode}/details`
    },
    {
      value: 'policies',
      className: classes.tab,
      label: getTranslate('Policies'),
      component: LocalizedLink,
      to: `/accommodations/${accommodationID}/${propertyCode}/room-type/${roomTypeID}/rate-plans/${id}/providerCode/${providerCode}/policies`
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = ratePlanPagesRoleManagement.find(
    (item) =>
      item.route ===
      '/accommodations/:accommodationID/:propertyCode/room-type/:roomTypeID/rate-plans/:id/providerCode/:providerCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  return (
    <div className={classes.root}>
      <AppBar position="static" color="transparent">
        <Breadcrumbs
          levels={cachedLevels}
          loading={ratePlanNameDataLoading || accommodationNameLoading}
        />
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth">
          {visibleTabs.map((item, index) => (
            <Tab
              key={index}
              value={item.value}
              className={item.className}
              label={item.label}
              component={item.component}
              to={item.to}
            />
          ))}
        </Tabs>
      </AppBar>
      <TabPanel
        value={page}
        index="details"
        dir={theme.direction}
        className={classes.tabPanel}>
        <RateplanForm />
      </TabPanel>
      <TabPanel
        value={page}
        index="policies"
        dir={theme.direction}
        className={classes.tabPanel}>
        <RatePlansPolicies providerCode={providerCode} />
      </TabPanel>
    </div>
  )
}
